import { CalendarCheck, TrendingUp, ListOrdered } from "lucide-react";
import { inr } from "@/lib/api";
import { PayoutLedgerDialog, scheduleLabel } from "@/components/PayoutLedger";

const DAY = 86400000;
const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const Card = ({ inv }) => {
  const start = new Date(inv.approved_at).getTime();
  const end = inv.maturity_date ? new Date(inv.maturity_date).getTime() : null;
  const now = Date.now();
  const total = end ? end - start : 0;
  const pct = inv.status === "completed" ? 100 : total > 0 ? Math.min(100, Math.max(0, ((now - start) / total) * 100)) : 0;
  const daysLeft = end ? Math.max(0, Math.ceil((end - now) / DAY)) : null;
  const matured = inv.status === "completed" || (end && now >= end);
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid={`maturity-card-${inv.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-accent text-[9px] tracking-[0.25em] uppercase text-white/35">{inv.category} · {inv.return_percent}% return</p>
          <p className="text-sm text-white mt-1 truncate">{inv.plan_title}</p>
        </div>
        <span className={`font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-1 shrink-0 ${matured ? "text-[#D4AF37] border-[#D4AF37]/40" : "text-emerald-400 border-emerald-400/30"}`} data-testid={`maturity-state-${inv.id}`}>
          {inv.status === "completed" ? "Paid out" : matured ? "Matured" : `${daysLeft} days left`}
        </span>
      </div>
      <div className="mt-5 h-1.5 bg-white/5 overflow-hidden" data-testid={`maturity-progress-${inv.id}`}>
        <div className="h-full bg-gradient-to-r from-[#8a6d1f] to-[#D4AF37] transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex justify-between font-accent text-[9px] tracking-[0.15em] uppercase text-white/35">
        <span>Started {fmt(inv.approved_at)}</span>
        <span>{Math.round(pct)}%</span>
        <span>{end ? `Matures ${fmt(inv.maturity_date)}` : "Term not set"}</span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-4 border-t border-white/10 pt-4">
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Invested</p>
          <p className="text-sm text-white mt-1">{inr(inv.amount)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1"><TrendingUp size={10} /> Interest</p>
          <p className="text-sm text-emerald-400 mt-1" data-testid={`maturity-interest-${inv.id}`}>+{inr(inv.expected_interest)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1"><CalendarCheck size={10} /> At maturity</p>
          <p className="text-sm text-[#D4AF37] mt-1 font-medium" data-testid={`maturity-amount-${inv.id}`}>{inr(inv.maturity_amount)}</p>
        </div>
      </div>
      <PayoutLedgerDialog inv={inv}>
        <button data-testid={`ledger-button-${inv.id}`} className="mt-4 w-full flex items-center justify-center gap-2 border border-white/15 py-2 font-accent text-[9px] tracking-[0.2em] uppercase text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          <ListOrdered size={11} /> {inv.credit_schedule === "daily" ? "Daily payout ledger" : scheduleLabel(inv.credit_schedule)} · view schedule
        </button>
      </PayoutLedgerDialog>
    </div>
  );
};

export const MaturityTracker = ({ investments }) => {
  const items = investments.filter((i) => (i.status === "active" || i.status === "completed") && i.approved_at);
  if (items.length === 0) return null;
  const interest = items.reduce((s, i) => s + (i.expected_interest || 0), 0);
  return (
    <section className="mt-16" data-testid="maturity-tracker">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h2 className="font-display text-2xl tracking-tight">Maturity Tracker</h2>
        <p className="text-xs text-white/40">Total expected interest <span className="text-emerald-400" data-testid="maturity-total-interest">+{inr(interest)}</span></p>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((inv) => <Card key={inv.id} inv={inv} />)}
      </div>
    </section>
  );
};
